import { useTranslations } from 'use-intl'
import { Check, Globe, Lock, Shield, Undo2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { Reveal } from '@/components/ui/reveal'

// ponytail: icons can't live in JSON — labels come from `home.dataOwnership.badges`,
// the icon per key stays here. Add a badge = add a key both places.
const BADGES: readonly { key: string; Icon: LucideIcon }[] = [
  { key: 'hosted', Icon: Shield },
  { key: 'encryption', Icon: Lock },
  { key: 'gdpr', Icon: Check },
  { key: 'standards', Icon: Globe },
  { key: 'reversibility', Icon: Undo2 },
]

/** Centred trust band: lavender `expert-band`, contained rounded card like the
    tailored section, headline + one-paragraph reassurance + a row of pill
    badges. Content-driven height, no `--landing-band-h`. */
export function DataOwnershipSection() {
  const t = useTranslations('home.dataOwnership')

  return (
    <section className="px-4 py-2 md:px-8">
      {/* One Reveal over the whole card — it rises and fades in as a single
          block, rather than cascading the heading/paragraph/badges. */}
      <Reveal className="relative isolate mx-auto max-w-7xl overflow-hidden rounded-3xl border border-border bg-expert-band">
        {/* Same rule grid as ExpertSection (`gridClassName="opacity-40"`). */}
        <div
          aria-hidden
          className="bg-grid-lines pointer-events-none absolute inset-0 -z-10 opacity-40"
        />
        <div className="px-6 py-14 text-center md:py-20 lg:px-8">
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-primary-vivid sm:text-3xl lg:text-4xl">
            {t('title')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl font-mono text-sm text-secondary-foreground">
            {t('description')}
          </p>
          <ul className="mt-10 flex flex-wrap justify-center gap-2.5 sm:gap-3">
            {BADGES.map(({ key, Icon }) => (
              <li
                key={key}
                className="inline-flex items-center gap-2.5 rounded-lg border border-primary/10 bg-white/80 py-1.5 pr-5 pl-1.5 text-sm font-medium text-accent-foreground backdrop-blur-sm"
              >
                {/* Icon in its own tinted chip — gives each badge a focal
                    point instead of a bare glyph floating next to text. */}
                <span className="flex size-7 items-center justify-center rounded-md bg-primary/10 text-primary-vivid">
                  <Icon className="size-4 shrink-0" aria-hidden />
                </span>
                {t(`badges.${key}`)}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>
  )
}
